import React, { createContext, useContext, useMemo } from 'react';

import { defaultLocale, getLocaleFromPath, isSupportedLocale } from './locales';
import {
  alternatePathFor as buildAlternatePathFor,
  pathFor as buildPathFor,
  projectPathFor as buildProjectPathFor
} from './routes';

function valueForKey(messages, key) {
  return key.split('.').reduce((value, part) => {
    if (value === undefined || value === null) {
      return undefined;
    }

    return value[part];
  }, messages);
}

function interpolate(message, values) {
  if (!values || typeof message !== 'string') {
    return message;
  }

  return Object.entries(values).reduce((text, [key, value]) => {
    return text.split(`{${key}}`).join(String(value));
  }, message);
}

const fallbackContext = {
  locale: defaultLocale,
  t: (key, fallback) => fallback ?? key,
  pathFor: (routeId) => buildPathFor(routeId, defaultLocale),
  projectPathFor: (slug) => buildProjectPathFor(slug, defaultLocale),
  alternatePathFor: (targetLocale, path = window.location.pathname) => buildAlternatePathFor(path, targetLocale),
};

const I18nContext = createContext(fallbackContext);

export function I18nProvider({ children, locale, messages = {}, path = window.location.pathname }) {
  const activeLocale = isSupportedLocale(locale) ? locale : getLocaleFromPath(path);

  const value = useMemo(() => {
    const activeMessages = messages[activeLocale] ?? {};

    return {
      locale: activeLocale,
      t: (key, fallback, values) => {
        const message = valueForKey(activeMessages, key) ?? fallback ?? key;
        return interpolate(message, values);
      },
      pathFor: (routeId) => buildPathFor(routeId, activeLocale),
      projectPathFor: (slug) => buildProjectPathFor(slug, activeLocale),
      alternatePathFor: (targetLocale, sourcePath = path) => buildAlternatePathFor(sourcePath, targetLocale),
    };
  }, [activeLocale, messages, path]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export default I18nContext;
